import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import api from "../lib/api";
import { toast } from "sonner";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import {
  MagnifyingGlass, Funnel, SquaresFour, ListBullets, Phone, Envelope, WhatsappLogo,
  X, CaretRight, CaretLeft, User, Calendar, Package, CheckCircle, XCircle,
  ClockCounterClockwise, ChatCircleDots, Trash, PlusCircle, ArrowSquareOut
} from "@phosphor-icons/react";
import { getCompanySettings, buildLeadFollowUpWaLink } from "../utils/whatsappHelper";

const STATUSES = [
  { key: "new", label: "New", color: "bg-blue-500", light: "bg-blue-50 border-blue-200", text: "text-blue-700" },
  { key: "contacted", label: "Contacted", color: "bg-indigo-500", light: "bg-indigo-50 border-indigo-200", text: "text-indigo-700" },
  { key: "follow_up", label: "Follow Up", color: "bg-amber-500", light: "bg-amber-50 border-amber-200", text: "text-amber-700" },
  { key: "won", label: "Won", color: "bg-emerald-500", light: "bg-emerald-50 border-emerald-200", text: "text-emerald-700" },
  { key: "lost", label: "Lost", color: "bg-rose-500", light: "bg-rose-50 border-rose-200", text: "text-rose-700" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}, ${d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`;
};
const relTime = (iso) => {
  if (!iso) return "";
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.floor(m / 60)}h ago`;
  return `${Math.floor(m / 1440)}d ago`;
};

export default function AdminProductLeads({ user, logout }) {
  const role = user?.role || "";
  const canAccess = ["admin", "manager", "bdm", "bde", "cfo"].includes(role);
  const canDelete = ["admin", "manager", "bdm", "cfo"].includes(role);

  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("kanban"); // kanban | table
  const [search, setSearch] = useState("");
  const [filterAssignee, setFilterAssignee] = useState("");
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null); // lead object for detail drawer
  const [dragging, setDragging] = useState(null);

  const fetchLeads = useCallback(async () => {
    if (!canAccess) return;
    setLoading(true);
    try {
      const params = { requester_role: role, requester_name: user?.name || "" };
      if (search) params.search = search;
      if (filterAssignee) params.assigned_to_id = filterAssignee;
      const [lRes, sRes] = await Promise.all([
        axios.get(`${API}/product-leads`, { params }),
        axios.get(`${API}/product-leads/stats`, { params: { requester_role: role, requester_name: user?.name || "" } }),
      ]);
      setLeads(lRes.data || []);
      setStats(sRes.data || null);
    } catch (err) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [canAccess, role, user?.name, search, filterAssignee]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);
  useEffect(() => {
    api.get(`/users`).then(r => setUsers((r.data || []).filter(u => u.role !== "customer"))).catch(() => {});
    getCompanySettings(); // prime WhatsApp template cache
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  // Deep-link: /admin/leads?lead=<id> opens the drawer for that lead
  useEffect(() => {
    const deepLead = searchParams.get("lead");
    if (deepLead && leads.length > 0 && selected?.lead_id !== deepLead) {
      const match = leads.find(l => l.lead_id === deepLead);
      if (match) setSelected(match);
    }
  }, [searchParams, leads, selected?.lead_id]);

  const grouped = useMemo(() => {
    const g = Object.fromEntries(STATUSES.map(s => [s.key, []]));
    for (const l of leads) {
      const k = g[l.status] ? l.status : "new";
      g[k].push(l);
    }
    return g;
  }, [leads]);

  const updateLead = async (leadId, patch) => {
    try {
      const { data } = await axios.put(`${API}/product-leads/${leadId}?requester_name=${encodeURIComponent(user?.name || "admin")}`, patch);
      setLeads(prev => prev.map(l => l.lead_id === leadId ? data.lead : l));
      if (selected?.lead_id === leadId) setSelected(data.lead);
      fetchLeads(); // refresh stats
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const onDragStart = (lead) => setDragging(lead);
  const onDropColumn = async (status) => {
    if (!dragging || dragging.status === status) { setDragging(null); return; }
    const leadId = dragging.lead_id;
    setDragging(null);
    await updateLead(leadId, { status, note: `Status changed to ${STATUSES.find(s => s.key === status)?.label}` });
    toast.success(`Moved to ${STATUSES.find(s => s.key === status)?.label}`);
  };

  const deleteLead = async (leadId) => {
    if (!canDelete) return;
    if (!window.confirm("Delete this lead? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/product-leads/${leadId}?requester_role=${role}`);
      toast.success("Lead deleted");
      setSelected(null);
      fetchLeads();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  if (!canAccess) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Sidebar user={user} logout={logout} />
        <div className="ml-64 p-12">
          <div className="bg-white border border-amber-200 p-8 text-center max-w-md mx-auto">
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Access Restricted</h2>
            <p className="text-sm text-slate-600">Lead Management is available to Admin, Manager, BDM and BDE only.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="ml-64">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
          <div className="px-6 py-4 flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>Product Leads</h1>
              <p className="text-xs text-slate-500 mt-0.5">Quick-quote pipeline · Drag cards between stages · Log notes and follow-ups</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="inline-flex border border-slate-200 rounded-sm">
                <button data-testid="view-kanban-btn" onClick={() => setView("kanban")} className={`px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 ${view === "kanban" ? "bg-[#002FA7] text-white" : "text-slate-600 hover:bg-slate-50"}`}><SquaresFour size={14} /> Kanban</button>
                <button data-testid="view-table-btn" onClick={() => setView("table")} className={`px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 ${view === "table" ? "bg-[#002FA7] text-white" : "text-slate-600 hover:bg-slate-50"}`}><ListBullets size={14} /> Table</button>
              </div>
            </div>
          </div>

          {/* KPI strip */}
          {stats && (
            <div className="px-6 pb-3 grid grid-cols-2 md:grid-cols-6 gap-2">
              <Kpi testid="kpi-total" label="Total Leads" value={stats.total} tone="slate" />
              {STATUSES.map(s => (
                <Kpi key={s.key} testid={`kpi-${s.key}`} label={s.label} value={stats.by_status?.[s.key] || 0} color={s.color} tone="light" />
              ))}
            </div>
          )}

          {/* Filters */}
          <div className="px-6 pb-3 flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="lead-search-input" type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search name, phone, email, product…"
                className="w-full pl-8 pr-3 py-2 border border-slate-300 text-sm focus:border-[#002FA7] focus:outline-none" />
            </div>
            <select data-testid="lead-filter-assignee" value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)}
              className="border border-slate-300 px-3 py-2 text-sm focus:border-[#002FA7] focus:outline-none">
              <option value="">All Assignees</option>
              {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name} ({u.role})</option>)}
            </select>
            {(search || filterAssignee) && (
              <button onClick={() => { setSearch(""); setFilterAssignee(""); }} className="text-xs text-[#002FA7] hover:underline font-bold">Clear</button>
            )}
            {stats && (stats.overdue_follow_ups > 0) && (
              <div className="ml-auto text-[11px] bg-amber-50 border border-amber-200 text-amber-800 px-3 py-1.5 rounded-sm flex items-center gap-1.5">
                <ClockCounterClockwise size={12} weight="fill" /> <b>{stats.overdue_follow_ups}</b> follow-up{stats.overdue_follow_ups !== 1 ? "s" : ""} overdue
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-4 md:p-6">
          {loading ? (
            <div className="text-center py-20 text-slate-500">Loading leads…</div>
          ) : view === "kanban" ? (
            <KanbanBoard grouped={grouped} onDragStart={onDragStart} onDropColumn={onDropColumn} onSelect={setSelected} dragging={dragging} />
          ) : (
            <LeadTable leads={leads} onSelect={setSelected} />
          )}
        </div>
      </div>

      {selected && (
        <LeadDrawer
          lead={selected}
          users={users}
          onClose={() => setSelected(null)}
          onUpdate={updateLead}
          onDelete={canDelete ? deleteLead : null}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, color, tone, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 px-3 py-2 flex items-center gap-2">
      {color && <span className={`w-2 h-8 ${color}`} />}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
        <div className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</div>
      </div>
    </div>
  );
}

/* -------- Kanban Board -------- */
function KanbanBoard({ grouped, onDragStart, onDropColumn, onSelect, dragging }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
      {STATUSES.map(s => (
        <div
          key={s.key}
          data-testid={`kanban-col-${s.key}`}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => onDropColumn(s.key)}
          className={`bg-white border-t-4 ${s.color.replace("bg-", "border-")} border-x border-b border-slate-200 min-h-[200px]`}
        >
          <div className={`px-3 py-2 flex items-center justify-between ${s.light} border-b ${s.light.split(" ")[1]}`}>
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${s.color}`} />
              <span className={`text-xs font-bold uppercase tracking-wider ${s.text}`}>{s.label}</span>
            </div>
            <span className="text-xs font-bold text-slate-600 bg-white px-1.5 py-0.5 rounded">{grouped[s.key].length}</span>
          </div>
          <div className="p-2 space-y-2 max-h-[calc(100vh-320px)] overflow-y-auto">
            {grouped[s.key].length === 0 ? (
              <div className="text-[11px] text-slate-400 text-center py-6 border border-dashed border-slate-200">No leads</div>
            ) : grouped[s.key].map(lead => (
              <KanbanCard key={lead.lead_id} lead={lead} onDragStart={() => onDragStart(lead)} onClick={() => onSelect(lead)} isDragging={dragging?.lead_id === lead.lead_id} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function KanbanCard({ lead, onDragStart, onClick, isDragging }) {
  const stale = lead.status === "follow_up" && lead.follow_up_at && new Date(lead.follow_up_at) < new Date();
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      data-testid={`kanban-card-${lead.lead_id}`}
      className={`bg-white border border-slate-200 hover:border-[#002FA7] hover:shadow p-2.5 cursor-grab active:cursor-grabbing transition-all ${isDragging ? "opacity-50" : ""} ${stale ? "ring-2 ring-amber-400" : ""}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="text-sm font-bold text-slate-900 truncate" style={{ fontFamily: "Outfit, sans-serif" }}>{lead.name}</div>
        <span className="text-[9px] text-slate-400 shrink-0">{relTime(lead.created_at)}</span>
      </div>
      <div className="text-[11px] text-slate-600 truncate mb-1 flex items-center gap-1"><Package size={10} /> {lead.product_name} · <b>×{lead.quantity}</b></div>
      <div className="flex flex-wrap gap-1 text-[10px] text-slate-500">
        {lead.phone && <span className="inline-flex items-center gap-0.5"><Phone size={9} />{lead.phone}</span>}
        {lead.company && <span className="inline-flex items-center gap-0.5">• {lead.company.slice(0, 18)}</span>}
      </div>
      <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-slate-100">
        <span className="text-[10px] text-slate-500 truncate">👤 {lead.assigned_to || "Unassigned"}</span>
        {stale && <span className="text-[9px] bg-amber-500 text-white px-1 py-0.5 font-bold">OVERDUE</span>}
      </div>
    </div>
  );
}

/* -------- Table view -------- */
function LeadTable({ leads, onSelect }) {
  if (leads.length === 0) return <div className="bg-white border border-slate-200 py-20 text-center text-sm text-slate-500">No leads found.</div>;
  return (
    <div className="bg-white border border-slate-200 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-bold">Customer</th>
            <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-bold">Product</th>
            <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-bold">Qty</th>
            <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-bold">Status</th>
            <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-bold">Assigned</th>
            <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-bold">Created</th>
            <th className="text-right px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-bold">Action</th>
          </tr>
        </thead>
        <tbody>
          {leads.map(l => {
            const s = STATUSES.find(x => x.key === l.status);
            return (
              <tr key={l.lead_id} data-testid={`row-lead-${l.lead_id}`} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => onSelect(l)}>
                <td className="px-3 py-2.5">
                  <div className="font-bold text-slate-900">{l.name}</div>
                  <div className="text-[11px] text-slate-500">{l.phone} {l.company && `· ${l.company}`}</div>
                </td>
                <td className="px-3 py-2.5 text-slate-700">{l.product_name}</td>
                <td className="px-3 py-2.5 font-bold">{l.quantity}</td>
                <td className="px-3 py-2.5"><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 ${s?.light} ${s?.text}`}>{s?.label}</span></td>
                <td className="px-3 py-2.5 text-slate-600">{l.assigned_to || "—"}</td>
                <td className="px-3 py-2.5 text-slate-500 text-xs">{fmtDate(l.created_at)}</td>
                <td className="px-3 py-2.5 text-right"><button onClick={(e) => { e.stopPropagation(); onSelect(l); }} className="text-[#002FA7] text-xs font-bold hover:underline">Open →</button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* -------- Detail Drawer -------- */
function LeadDrawer({ lead, users, onClose, onUpdate, onDelete }) {
  const [note, setNote] = useState("");
  const [followUp, setFollowUp] = useState(lead.follow_up_at ? lead.follow_up_at.slice(0, 16) : "");
  const [wonAmount, setWonAmount] = useState(lead.won_amount || "");
  const [lostReason, setLostReason] = useState(lead.lost_reason || "");

  useEffect(() => {
    setFollowUp(lead.follow_up_at ? lead.follow_up_at.slice(0, 16) : "");
    setWonAmount(lead.won_amount || "");
    setLostReason(lead.lost_reason || "");
  }, [lead.lead_id, lead.follow_up_at, lead.won_amount, lead.lost_reason]);

  const s = STATUSES.find(x => x.key === lead.status);
  const submitNote = async () => {
    if (!note.trim()) return;
    await onUpdate(lead.lead_id, { note: note.trim() });
    setNote("");
    toast.success("Note logged");
  };
  const changeStatus = (status) => onUpdate(lead.lead_id, { status });
  const saveFollowUp = () => {
    if (!followUp) { toast.error("Pick a date/time"); return; }
    onUpdate(lead.lead_id, { follow_up_at: new Date(followUp).toISOString(), status: "follow_up", note: `Follow-up scheduled for ${followUp}` });
  };
  const assign = (uid) => {
    const u = users.find(x => x.user_id === uid);
    if (!u) return;
    onUpdate(lead.lead_id, { assigned_to: u.name, assigned_to_id: u.user_id, note: `Re-assigned to ${u.name}` });
  };
  const waLink = buildLeadFollowUpWaLink(lead);

  return (
    <div data-testid="lead-drawer" className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <aside className="relative w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{lead.lead_id}</div>
            <h3 className="text-lg font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>{lead.name}</h3>
          </div>
          <button data-testid="drawer-close" onClick={onClose} className="p-1 hover:bg-slate-100"><X size={18} /></button>
        </div>

        {/* Status pill */}
        <div className="px-5 pt-4">
          <div className="flex items-center gap-2 mb-3">
            <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-1 ${s?.light} ${s?.text} border ${s?.light.split(" ")[1]}`}>● {s?.label}</span>
            <span className="text-[11px] text-slate-500">{fmtDate(lead.created_at)}</span>
          </div>

          <div className="flex flex-wrap gap-1 mb-4">
            {STATUSES.map(st => (
              <button key={st.key} data-testid={`drawer-status-${st.key}`} onClick={() => changeStatus(st.key)}
                className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 border transition-all ${lead.status === st.key ? `${st.color} text-white ${st.color.replace("bg-", "border-")}` : `bg-white text-slate-600 border-slate-300 hover:${st.light}`}`}>
                {st.label}
              </button>
            ))}
          </div>
        </div>

        {/* Customer info */}
        <Section title="Customer">
          <InfoRow icon={<User size={12} />} label="Name" value={lead.name} />
          <InfoRow icon={<Phone size={12} />} label="Phone" value={lead.phone} action={lead.phone && <a href={`tel:${lead.phone}`} className="text-[#002FA7] hover:underline text-xs">Call</a>} />
          <InfoRow icon={<Envelope size={12} />} label="Email" value={lead.email || "—"} action={lead.email && <a href={`mailto:${lead.email}`} className="text-[#002FA7] hover:underline text-xs">Email</a>} />
          <InfoRow icon={<Package size={12} />} label="Company" value={lead.company || "—"} />
          {waLink && (
            <a data-testid="drawer-whatsapp" href={waLink} target="_blank" rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1.5 text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 font-bold">
              <WhatsappLogo size={12} weight="fill" /> Open WhatsApp
            </a>
          )}
        </Section>

        {/* Product */}
        <Section title="Product Interest">
          <InfoRow label="Product" value={lead.product_name} />
          <InfoRow label="Quantity" value={<b className="text-[#002FA7]">{lead.quantity}</b>} />
          <InfoRow label="Source" value={lead.source} />
          {lead.message && <div className="mt-2 text-xs bg-slate-50 border border-slate-200 p-2 whitespace-pre-wrap">{lead.message}</div>}
        </Section>

        {/* Assignment */}
        <Section title="Assignment">
          <div className="text-xs text-slate-600 mb-2">Currently: <b>{lead.assigned_to || "Unassigned"}</b></div>
          <select data-testid="drawer-assign-select" value={lead.assigned_to_id || ""} onChange={e => assign(e.target.value)}
            className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-[#002FA7] focus:outline-none">
            <option value="">Unassigned</option>
            {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name} — {u.role}</option>)}
          </select>
        </Section>

        {/* Follow-up */}
        <Section title="Schedule Follow-up">
          <div className="flex gap-2">
            <input data-testid="drawer-followup-date" type="datetime-local" value={followUp} onChange={e => setFollowUp(e.target.value)}
              className="flex-1 border border-slate-300 px-3 py-2 text-sm focus:border-[#002FA7] focus:outline-none" />
            <button data-testid="drawer-followup-save" onClick={saveFollowUp} className="bg-amber-500 hover:bg-amber-600 text-white px-4 text-xs font-bold flex items-center gap-1.5">
              <Calendar size={12} /> Set
            </button>
          </div>
          {lead.follow_up_at && (
            <div className="text-[11px] text-slate-500 mt-1.5">Scheduled: <b>{fmtDate(lead.follow_up_at)}</b>{new Date(lead.follow_up_at) < new Date() && <span className="text-amber-600 font-bold ml-2">(Overdue)</span>}</div>
          )}
        </Section>

        {/* Won / Lost inputs conditional */}
        {(lead.status === "won" || lead.status === "lost") && (
          <Section title={lead.status === "won" ? "Won Details" : "Lost Details"}>
            {lead.status === "won" ? (
              <div className="flex gap-2 items-center">
                <span className="text-xs text-slate-600">Amount ₹</span>
                <input data-testid="drawer-won-amount" type="number" value={wonAmount} onChange={e => setWonAmount(e.target.value)}
                  onBlur={() => onUpdate(lead.lead_id, { won_amount: parseFloat(wonAmount) || 0, note: `Won ₹${wonAmount}` })}
                  className="flex-1 border border-slate-300 px-3 py-1.5 text-sm focus:border-[#002FA7] focus:outline-none" />
              </div>
            ) : (
              <textarea data-testid="drawer-lost-reason" value={lostReason} onChange={e => setLostReason(e.target.value)}
                onBlur={() => onUpdate(lead.lead_id, { lost_reason: lostReason, note: `Lost — ${lostReason}` })}
                rows={2} placeholder="Why was this lost? (price / competitor / timing…)"
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-[#002FA7] focus:outline-none" />
            )}
          </Section>
        )}

        {/* Notes history */}
        <Section title={`Activity (${(lead.notes_history || []).length})`}>
          <div className="space-y-2 mb-3 max-h-60 overflow-y-auto">
            {(lead.notes_history || []).length === 0 ? (
              <div className="text-[11px] text-slate-400 italic">No activity logged yet.</div>
            ) : (lead.notes_history || []).slice().reverse().map((n, i) => (
              <div key={`n-${i}-${n.at}`} className="text-xs bg-slate-50 border-l-2 border-[#002FA7] px-2 py-1.5">
                <div className="text-slate-900">{n.note}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">by {n.by} · {fmtDate(n.at)} {n.status_after && `· → ${n.status_after}`}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input data-testid="drawer-note-input" type="text" value={note} onChange={e => setNote(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") submitNote(); }}
              placeholder="Add a note and hit Enter…"
              className="flex-1 border border-slate-300 px-3 py-2 text-sm focus:border-[#002FA7] focus:outline-none" />
            <button data-testid="drawer-note-save" onClick={submitNote} className="bg-[#002FA7] hover:bg-[#00227A] text-white px-3 text-xs font-bold flex items-center gap-1">
              <ChatCircleDots size={12} /> Log
            </button>
          </div>
        </Section>

        {/* Delete (admin/manager/bdm) */}
        {onDelete && (
          <div className="px-5 pb-6 pt-2 border-t border-slate-100">
            <button data-testid="drawer-delete-btn" onClick={() => onDelete(lead.lead_id)} className="text-xs text-rose-600 hover:text-rose-700 font-bold inline-flex items-center gap-1.5">
              <Trash size={12} /> Delete Lead
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="px-5 py-3 border-b border-slate-100">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">{title}</div>
      {children}
    </div>
  );
}

function InfoRow({ icon, label, value, action }) {
  return (
    <div className="flex items-center justify-between py-1 text-xs">
      <span className="inline-flex items-center gap-1.5 text-slate-500 w-24 shrink-0">{icon}{label}</span>
      <span className="text-slate-900 text-right flex-1 truncate">{value}</span>
      {action && <span className="ml-2">{action}</span>}
    </div>
  );
}
